(function () {
  "use strict";

  window.BoaBranchPicker = window.BoaBranchPicker || {};

  // ============================================================================
  // Address Data (cities and streets from the Verifone address DB)
  // ============================================================================
  const ADDRESS_API_BASE = "/apps/proxy/address";
  const MAX_RENDERED_OPTIONS = 50;
  // The same street is stored under inconsistent punctuation (א.ד גורדון / א.ד. גורדון),
  // so matching ignores these while the stored string stays what we display and submit.
  const IGNORED_MATCH_CHARS = "'\"`׳״.-";

  const cityRequestsByLang = new Map();
  const streetRequestsByCity = new Map();
  const normalizedLists = new WeakMap();

  window.BoaBranchPicker.addressData = {
    getLang: function () {
      const form = document.getElementById("boa-branch-picker-form");
      const locale = form?.getAttribute("data-locale");
      return locale === "he" || locale === "ar" ? "he" : "en";
    },

    // The two languages are separate value sets, so the typed script picks the set (a
    // Hebrew-locale customer can type "Jerusalem"); a city's own script picks its streets'.
    detectLang: function (value) {
      if (/[\u0590-\u05FF]/.test(value)) {
        return "he";
      }
      if (/[A-Za-z]/.test(value)) {
        return "en";
      }
      return this.getLang();
    },

    fetchList: async function (path, key) {
      const response = await fetch(path, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) {
        throw new Error(`Address request failed: ${response.status}`);
      }
      const data = await response.json();
      return Array.isArray(data?.[key]) ? data[key] : [];
    },

    // Caches the in-flight promise, so keystrokes during the first fetch share one request.
    cachedRequest: function (cache, key, fetcher) {
      const cached = cache.get(key);
      if (cached) {
        return cached;
      }
      const request = fetcher().catch((error) => {
        cache.delete(key);
        throw error;
      });
      cache.set(key, request);
      return request;
    },

    getCities: function (lang) {
      return this.cachedRequest(cityRequestsByLang, lang, () =>
        this.fetchList(`${ADDRESS_API_BASE}/cities?lang=${lang}`, "cities")
      );
    },

    getStreets: function (city) {
      return this.cachedRequest(streetRequestsByCity, city, () =>
        this.fetchList(
          `${ADDRESS_API_BASE}/streets?city=${encodeURIComponent(
            city
          )}&lang=${this.detectLang(city)}`,
          "streets"
        ).then(this.numberedStreetsLast)
      );
    },

    // Verifone names a fair number of streets with digits ("10", "3000 סמטה 2"). Sorted
    // as text they lead the list and bury the real names when the field is opened empty.
    numberedStreetsLast: function (streets) {
      const named = [];
      const numbered = [];
      streets.forEach((street) => {
        (/^\d/.test(street.trim()) ? numbered : named).push(street);
      });
      return named.concat(numbered);
    },

    // Normalised copy plus, for each normalised character, its index in the original,
    // so a match found here can be highlighted on the string the customer actually sees.
    buildNormalized: function (value) {
      let normalized = "";
      const indexMap = [];
      let previousWasSpace = false;

      for (let index = 0; index < value.length; index += 1) {
        const character = value[index];
        if (IGNORED_MATCH_CHARS.includes(character)) {
          continue;
        }
        if (/\s/.test(character)) {
          if (normalized.length === 0 || previousWasSpace) {
            continue;
          }
          normalized += " ";
          indexMap.push(index);
          previousWasSpace = true;
          continue;
        }
        normalized += character.toLowerCase();
        indexMap.push(index);
        previousWasSpace = false;
      }

      if (previousWasSpace) {
        normalized = normalized.slice(0, -1);
        indexMap.pop();
      }

      return { normalized: normalized, indexMap: indexMap };
    },

    normalizeQuery: function (query) {
      return this.buildNormalized(query).normalized;
    },

    // Normalising is per-list work, not per-keystroke: the lists are immutable once fetched,
    // and the WeakMap lets a discarded list take its normalised copy with it.
    getNormalizedList: function (values) {
      let normalized = normalizedLists.get(values);
      if (!normalized) {
        normalized = values.map((value) => this.buildNormalized(value));
        normalizedLists.set(values, normalized);
      }
      return normalized;
    },

    filterMatches: function (values, query) {
      const normalizedQuery = this.normalizeQuery(query);

      if (!normalizedQuery) {
        return values.slice(0, MAX_RENDERED_OPTIONS).map((value) => ({
          value: value,
          label: value,
          matchedSubstrings: [],
        }));
      }

      const startsWithQuery = [];
      const containsQuery = [];
      const normalizedValues = this.getNormalizedList(values);

      values.forEach((value, index) => {
        const built = normalizedValues[index];
        const position = built.normalized.indexOf(normalizedQuery);
        if (position === -1) {
          return;
        }

        // End at the last matched character, not the start of the next one, so stripped
        // punctuation sitting between them is not highlighted too.
        const offset = built.indexMap[position];
        const end = built.indexMap[position + normalizedQuery.length - 1] + 1;

        const match = {
          value: value,
          label: value,
          matchedSubstrings: [{ offset: offset, length: end - offset }],
        };
        (position === 0 ? startsWithQuery : containsQuery).push(match);
      });

      return startsWithQuery
        .concat(containsQuery)
        .slice(0, MAX_RENDERED_OPTIONS);
    },
  };

  // ============================================================================
  // Date Utils
  // ============================================================================
  let boaSpecialDateRanges = [];
  let boaSpecialDateRangesParsedFromDom = false;

  function boaParseIsoDateLocal(isoString) {
    if (typeof isoString !== "string") {
      return null;
    }
    const parts = isoString.split("-");
    if (parts.length !== 3) {
      return null;
    }
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  window.BoaBranchPicker.dateUtils = {
    loadSpecialDateRangesFromDom: function () {
      if (boaSpecialDateRangesParsedFromDom) {
        return;
      }
      boaSpecialDateRangesParsedFromDom = true;
      const element = document.getElementById("boa-special-date-ranges");
      if (!element || !element.textContent) {
        boaSpecialDateRanges = [];
        return;
      }
      try {
        const parsed = JSON.parse(element.textContent.trim());
        boaSpecialDateRanges = Array.isArray(parsed) ? parsed : [];
      } catch {
        boaSpecialDateRanges = [];
      }
    },

    isDateInSpecialBlockedRange: function (isoDate) {
      const check = boaParseIsoDateLocal(isoDate);
      if (!check) {
        return false;
      }
      const checkTime = check.getTime();
      for (let i = 0; i < boaSpecialDateRanges.length; i++) {
        const range = boaSpecialDateRanges[i];
        if (
          !range ||
          typeof range.start !== "string" ||
          typeof range.end !== "string"
        ) {
          continue;
        }
        let start = boaParseIsoDateLocal(range.start);
        let end = boaParseIsoDateLocal(range.end);
        if (!start || !end) {
          continue;
        }
        if (end < start) {
          const swap = start;
          start = end;
          end = swap;
        }
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        const startTime = start.getTime();
        const endTime = end.getTime();
        if (checkTime >= startTime && checkTime <= endTime) {
          return true;
        }
      }
      return false;
    },

    formatDateToISO: function (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    getLocaleConfig: function () {
      const form = document.getElementById("boa-branch-picker-form");
      const locale =
        form?.dataset.locale || document.documentElement.lang || "en";
      const isHebrewOrArabic = locale === "he" || locale === "ar";

      if (!isHebrewOrArabic) {
        return null;
      }

      return {
        customDays: ["א", "ב", "ג", "ד", "ה", "ו", "ש"],
        customMonths: [
          "ינואר",
          "פברואר",
          "מרץ",
          "אפריל",
          "מאי",
          "יוני",
          "יולי",
          "אוגוסט",
          "ספטמבר",
          "אוקטובר",
          "נובמבר",
          "דצמבר",
        ],
      };
    },

    isDateDisabled: function (date) {
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const now = new Date();

      const isSaturday = checkDate.getDay() === 6;
      const isToday = checkDate.getTime() === today.getTime();
      const isPastSevenPM = now.getHours() >= 19;

      if (isSaturday || (isToday && isPastSevenPM)) {
        return true;
      }

      const isoDate = this.formatDateToISO(checkDate);
      return this.isDateInSpecialBlockedRange(isoDate);
    },

    handleDateSelection: function (date) {
      if (!date) return;

      const domUtils = window.BoaBranchPicker.domUtils;
      const displayInput = domUtils.getInputElement("boa-date-display");
      const hiddenInput = domUtils.getInputElement("boa-date");
      if (!displayInput || !hiddenInput) return;

      const isoDate = this.formatDateToISO(date);
      hiddenInput.value = isoDate;
      // The picker already applied this date. Suppress the re-entrant setDate()
      // in handleDateInputChange that would re-render the calendar mid-selection
      // and swallow the click (requiring a second click to register).
      this.suppressSetDate = true;
      hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
      this.suppressSetDate = false;

      displayInput.closest(".input")?.classList.add("is-filled");

      this.updateQuickDateButtonStates(date);
      const formUtils = window.BoaBranchPicker.formUtils;
      if (formUtils && formUtils.showOptionSpecificFields) {
        formUtils.showOptionSpecificFields();
      }
      if (formUtils && formUtils.updateSubmitButtonState) {
        formUtils.updateSubmitButtonState();
      }
    },

    updateQuickDateButtonStates: function (selectedDate) {
      if (!selectedDate) return;

      const constants = window.BoaBranchPicker.constants;
      const selectedIsoDate = this.formatDateToISO(selectedDate);
      const dateButtons = document.querySelectorAll(
        constants.SELECTORS.dateOption
      );

      dateButtons.forEach((dateButton) => {
        const isSelected =
          dateButton.getAttribute("data-date-value") === selectedIsoDate;
        dateButton.classList.toggle("is-selected", isSelected);
      });
    },

    initDatePicker: function () {
      const domUtils = window.BoaBranchPicker.domUtils;
      const state = window.BoaBranchPicker.state;

      const displayInput = domUtils.getInputElement("boa-date-display");
      const hiddenInput = domUtils.getInputElement("boa-date");
      if (!displayInput || !hiddenInput) {
        return;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const localeConfig = this.getLocaleConfig();

      const config = {
        minDate: today,
        disabler: (date) => this.isDateDisabled(date),
        formatter: (dateInput, dateValue) => {
          const day = String(dateValue.getDate()).padStart(2, "0");
          const month = String(dateValue.getMonth() + 1).padStart(2, "0");
          const year = dateValue.getFullYear();
          const formatted = `${day}/${month}/${year}`;
          dateInput.value = formatted;
          return formatted;
        },
        onSelect: (datePickerInstance, selectedDate) => {
          this.handleDateSelection(selectedDate);
        },
        position: "c",
      };

      if (localeConfig) {
        config.customDays = localeConfig.customDays;
        config.customMonths = localeConfig.customMonths;
      }

      if (typeof window["datepicker"] !== "undefined") {
        const datePickerInstance = window["datepicker"](
          "#boa-date-display",
          config
        );
        state.setDatePickerInstance(datePickerInstance);

        // Workaround for datepicker bug: with position "c", the picker closes immediately because
        // the library's click handler doesn't recognize the instance when it overlays the input.
        // See: https://github.com/qodesmith/datepicker/issues/148

        const handleInputClick = (event) => {
          event.stopPropagation();
          event.preventDefault();
          setTimeout(() => {
            try {
              datePickerInstance.show();
            } catch (error) {
              console.error("Error showing datepicker:", error);
            }
          }, 0);
        };

        const handleInputFocus = (event) => {
          event.stopPropagation();
          event.preventDefault();
          setTimeout(() => {
            try {
              datePickerInstance.show();
            } catch (error) {
              console.error("Error showing datepicker:", error);
            }
          }, 0);
        };

        const handleInputBlur = (event) => {
          const datepickerContainer = document.querySelector(
            ".qs-datepicker-container"
          );
          const isDatepickerVisible =
            datepickerContainer &&
            datepickerContainer.classList.contains("qs-active");
          const isCentered =
            datepickerContainer &&
            datepickerContainer.classList.contains("qs-centered");

          if (isDatepickerVisible && isCentered) {
            event.stopImmediatePropagation();
            event.preventDefault();
            setTimeout(() => {
              const stillVisible =
                datepickerContainer &&
                datepickerContainer.classList.contains("qs-active");
              if (document.activeElement !== displayInput && stillVisible) {
                displayInput.focus();
              }
            }, 0);
          }
        };

        displayInput.addEventListener("click", handleInputClick, true);
        displayInput.addEventListener("focus", handleInputFocus, true);
        displayInput.addEventListener("blur", handleInputBlur, true);

        let isShowing = false;
        let showTimeout = null;

        const originalShow = datePickerInstance.show;
        const originalHide =
          datePickerInstance.hide || datePickerInstance.destroy;

        let hideBlocked = false;
        if (originalHide) {
          datePickerInstance.hide = function () {
            if (hideBlocked) {
              return;
            }
            return originalHide.call(this);
          };
        }

        datePickerInstance.show = function () {
          try {
            hideBlocked = true;
            isShowing = true;
            clearTimeout(showTimeout);

            originalShow.call(this);

            setTimeout(() => {
              hideBlocked = false;
            }, 150);

            showTimeout = setTimeout(() => {
              isShowing = false;
            }, 300);
          } catch (error) {
            hideBlocked = false;
            isShowing = false;
            clearTimeout(showTimeout);
            console.error("Error in datepicker show():", error);
          }
        };

        const documentClickInterceptor = (event) => {
          const datepickerContainer = document.querySelector(
            ".qs-datepicker-container"
          );
          const isDatepickerVisible =
            datepickerContainer &&
            datepickerContainer.classList.contains("qs-active");
          const isCentered =
            datepickerContainer &&
            datepickerContainer.classList.contains("qs-centered");
          const clickedInsideDatepicker =
            datepickerContainer &&
            (datepickerContainer.contains(event.target) ||
              datepickerContainer === event.target);
          const clickedOnInput =
            event.target === displayInput ||
            displayInput.contains(event.target);
          const clickedOnInputParent =
            displayInput.parentElement &&
            (event.target === displayInput.parentElement ||
              displayInput.parentElement.contains(event.target));

          const shouldProtect =
            (isShowing || isDatepickerVisible) && isCentered;

          if (shouldProtect) {
            // Allow all clicks inside datepicker to proceed (for date selection, navigation, etc.)
            // Only block clicks on the input or its parent that would trigger close during opening
            if (clickedInsideDatepicker) {
              return; // Let datepicker handle its own clicks
            }

            // Block clicks on input/parent that would close picker during opening
            if (clickedOnInput || clickedOnInputParent) {
              event.stopImmediatePropagation();
              return;
            }
          }
        };

        document.addEventListener("click", documentClickInterceptor, true);
      } else {
        console.error("Datepicker library not available");
      }
    },

    populateDateValues: function () {
      this.loadSpecialDateRangesFromDom();

      const domUtils = window.BoaBranchPicker.domUtils;

      const today = new Date();
      const dateInput = domUtils.getInputElement("boa-date");
      const dateDisplay = domUtils.getInputElement("boa-date-display");
      const dateButtons = document.querySelectorAll("[data-date-option]");

      if (!dateInput) return;

      dateInput.min = this.formatDateToISO(today);

      const optionToOffset = {
        today: 0,
        tomorrow: 1,
        "day-after-tomorrow": 2,
      };

      const now = new Date();
      const isPastSevenPM = now.getHours() >= 19;

      dateButtons.forEach((dateButton) => {
        const optionType = dateButton.getAttribute("data-date-option");
        const offset = optionToOffset[optionType];
        if (offset === undefined) return;

        const date = new Date(today);
        date.setDate(today.getDate() + offset);
        const dateValue = this.formatDateToISO(date);

        dateButton.setAttribute("data-date-value", dateValue);

        const dateSpan = dateButton.querySelector(`[data-date-${optionType}]`);
        if (dateSpan) {
          dateSpan.textContent = dateValue
            .slice(5, 10)
            .split("-")
            .reverse()
            .join(".");
        }

        const isDisabled = this.isDateDisabled(date);
        if (dateButton instanceof HTMLButtonElement) {
          dateButton.disabled = isDisabled;
        }
        dateButton.toggleAttribute("data-disabled", isDisabled);
      });

      if (dateDisplay && !dateInput.value) {
        dateDisplay.value = "";
        dateDisplay.classList.remove("is-filled");
      }
    },

    updateDateDisplay: function (dateInputElement) {
      const constants = window.BoaBranchPicker.constants;
      const domUtils = window.BoaBranchPicker.domUtils;

      const dateDisplay = domUtils.getInputElement("boa-date-display");
      if (dateDisplay) {
        const inputContainer = dateDisplay.closest(".input");
        if (dateInputElement.value) {
          inputContainer?.classList.add("is-filled");
        } else {
          dateDisplay.value = "";
          inputContainer?.classList.remove("is-filled");
        }
      }

      document
        .querySelectorAll(constants.SELECTORS.dateOption)
        .forEach((dateOption) => dateOption.classList.remove("is-selected"));

      const formUtils = window.BoaBranchPicker.formUtils;
      if (dateInputElement.value) {
        if (formUtils && formUtils.showOptionSpecificFields) {
          formUtils.showOptionSpecificFields();
        }
      } else {
        if (formUtils && formUtils.hideOptionSpecificFields) {
          formUtils.hideOptionSpecificFields();
        }
      }

      if (formUtils && formUtils.updateSubmitButtonState) {
        formUtils.updateSubmitButtonState();
      }
    },

    syncDateDisplayOnBlur: function () {
      const domUtils = window.BoaBranchPicker.domUtils;
      const formUtils = window.BoaBranchPicker.formUtils;

      const dateDisplay = domUtils.getInputElement("boa-date-display");
      const hiddenDateInput = domUtils.getInputElement("boa-date");

      if (!dateDisplay || !hiddenDateInput) {
        return;
      }

      dateDisplay.addEventListener("blur", () => {
        const hiddenValue = hiddenDateInput.value;

        if (hiddenValue) {
          const dateValue = new Date(hiddenValue + "T00:00:00");
          if (!isNaN(dateValue.getTime())) {
            this.handleDateSelection(dateValue);
          }
        } else {
          dateDisplay.value = "";
          dateDisplay.closest(".input")?.classList.remove("is-filled");
          formUtils?.hideOptionSpecificFields();
          formUtils?.updateSubmitButtonState();
        }
      });
    },
  };

  // ============================================================================
  // Select Utils
  // ============================================================================
  window.BoaBranchPicker.selectUtils = {
    // City and street are free-text typeaheads: a text input with a dropdown rendered
    // outside the field, unlike the button-triggered selects used elsewhere.
    isTypeaheadSearch: function (searchType) {
      return searchType === "city" || searchType === "street";
    },

    getTypeaheadDropdown: function (selectElement) {
      const searchType = selectElement.getAttribute("data-search-type");
      const wrapper = selectElement.closest(
        ".boa-branch-picker__recipient-type-wrapper"
      );
      return wrapper?.querySelector(`[data-address-dropdown="${searchType}"]`);
    },

    // Popup visibility and aria-expanded must move together, or the field announces a list
    // that is not showing.
    setTypeaheadOpen: function (selectElement, isOpen) {
      const constants = window.BoaBranchPicker.constants;
      const searchInput = selectElement.querySelector(
        constants.SELECTORS.searchInput
      );
      this.getTypeaheadDropdown(selectElement)?.toggleAttribute(
        "data-open",
        isOpen
      );
      searchInput?.setAttribute("aria-expanded", String(isOpen));
      if (!isOpen) {
        searchInput?.removeAttribute("aria-activedescendant");
      }
    },

    getSelectedCity: function () {
      const constants = window.BoaBranchPicker.constants;
      const domUtils = window.BoaBranchPicker.domUtils;
      return (
        domUtils.getInputElement(constants.IDS.cityInput)?.value.trim() || ""
      );
    },

    getTypeaheadOptions: function (selectElement) {
      const constants = window.BoaBranchPicker.constants;
      const dropdown = this.getTypeaheadDropdown(selectElement);
      return Array.from(
        dropdown?.querySelectorAll(constants.SELECTORS.searchableOption) || []
      );
    },

    getActiveTypeaheadOption: function (selectElement) {
      return (
        this.getTypeaheadOptions(selectElement).find((option) =>
          option.hasAttribute("data-active")
        ) || null
      );
    },

    moveTypeaheadActive: function (selectElement, step) {
      const options = this.getTypeaheadOptions(selectElement);
      if (!options.length) return;

      const currentIndex = options.findIndex((option) =>
        option.hasAttribute("data-active")
      );
      const nextIndex =
        currentIndex === -1
          ? step > 0
            ? 0
            : options.length - 1
          : (currentIndex + step + options.length) % options.length;

      const constants = window.BoaBranchPicker.constants;
      options.forEach((option) => option.removeAttribute("data-active"));
      options[nextIndex].setAttribute("data-active", "");
      options[nextIndex].scrollIntoView({ block: "nearest" });
      selectElement
        .querySelector(constants.SELECTORS.searchInput)
        ?.setAttribute("aria-activedescendant", options[nextIndex].id);
    },

    setFieldState: function (hiddenInput, selectElement, enabled) {
      const constants = window.BoaBranchPicker.constants;

      if (hiddenInput) {
        hiddenInput.disabled = !enabled;
        if (enabled) hiddenInput.removeAttribute("aria-disabled");
      }
      if (selectElement) {
        selectElement.toggleAttribute("data-disabled", !enabled);
        const searchType = selectElement.getAttribute("data-search-type");
        if (this.isTypeaheadSearch(searchType)) {
          const searchInput = selectElement.querySelector(
            constants.SELECTORS.searchInput
          );
          if (searchInput instanceof HTMLInputElement) {
            searchInput.disabled = !enabled;
            if (enabled) searchInput.removeAttribute("aria-disabled");
          }
        } else {
          const selectTrigger = selectElement.querySelector(
            `${constants.SELECTORS.customTrigger}, ${constants.SELECTORS.searchableTrigger}`
          );
          if (selectTrigger instanceof HTMLButtonElement) {
            selectTrigger.disabled = !enabled;
            if (enabled) selectTrigger.removeAttribute("aria-disabled");
          }
        }
      }
    },

    resetSelectValue: function (
      selectElement,
      inputElement,
      valueDisplaySelector
    ) {
      if (inputElement instanceof HTMLInputElement) inputElement.value = "";
      const valueDisplay = selectElement.querySelector(valueDisplaySelector);
      if (valueDisplay) valueDisplay.textContent = "";
    },

    resetCustomSelectValue: function (selectElement, inputElement) {
      const constants = window.BoaBranchPicker.constants;
      this.resetSelectValue(
        selectElement,
        inputElement,
        constants.SELECTORS.customValue
      );
    },

    resetSearchableSelectValue: function (selectElement, inputElement) {
      const constants = window.BoaBranchPicker.constants;
      const searchType = selectElement.getAttribute("data-search-type");
      if (this.isTypeaheadSearch(searchType)) {
        const searchInput = selectElement.querySelector(
          constants.SELECTORS.searchInput
        );
        if (searchInput instanceof HTMLInputElement) {
          searchInput.value = "";
        }
        if (inputElement instanceof HTMLInputElement) {
          inputElement.value = "";
        }
        selectElement.closest(".input")?.classList.remove("is-filled");
      } else {
        this.resetSelectValue(
          selectElement,
          inputElement,
          constants.SELECTORS.searchableValue
        );
      }
    },

    toggleSelect: function (selectElement) {
      if (selectElement.hasAttribute("data-open")) {
        this.closeSelect(selectElement);
      } else {
        this.closeAllSelects();
        this.openSelect(selectElement);
      }
    },

    openSelect: function (selectElement) {
      const constants = window.BoaBranchPicker.constants;
      const domUtils = window.BoaBranchPicker.domUtils;
      const state = window.BoaBranchPicker.state;

      selectElement.setAttribute("data-open", "");
      const searchType = selectElement.getAttribute("data-search-type");
      if (this.isTypeaheadSearch(searchType)) {
        const searchInput = selectElement.querySelector(
          constants.SELECTORS.searchInput
        );
        const wrapper = selectElement.closest(
          ".boa-branch-picker__recipient-type-wrapper"
        );
        // Positioned on open but left closed; loadSearchResults opens it if it has results.
        const dropdown = this.getTypeaheadDropdown(selectElement);
        if (dropdown && searchInput) {
          const inputRect = searchInput.getBoundingClientRect();
          const wrapperRect = wrapper.getBoundingClientRect();
          const dropdownOffset = 4;
          dropdown.style.top = `${
            inputRect.bottom - wrapperRect.top + dropdownOffset
          }px`;
          dropdown.style.left = `${inputRect.left - wrapperRect.left}px`;
          dropdown.style.width = `${inputRect.width}px`;
        }
      } else {
        selectElement
          .querySelector(domUtils.getSelectTriggerSelector(selectElement))
          ?.setAttribute("aria-expanded", "true");
      }
      if (!domUtils.isCustomSelect(selectElement)) {
        const searchInput = selectElement.querySelector(
          constants.SELECTORS.searchInput
        );
        if (searchInput && !this.isTypeaheadSearch(searchType)) {
          setTimeout(() => searchInput.focus(), 0);
        }
        if (searchType) {
          const currentQuery =
            this.isTypeaheadSearch(searchType) && searchInput
              ? searchInput.value.trim()
              : "";
          this.loadSearchResults(selectElement, currentQuery, searchType);
        }
      }

      const datePickerInstance = state?.getDatePickerInstance();
      datePickerInstance?.hide();
    },

    closeSelect: function (selectElement) {
      const constants = window.BoaBranchPicker.constants;
      const domUtils = window.BoaBranchPicker.domUtils;

      selectElement.removeAttribute("data-open");
      const searchType = selectElement.getAttribute("data-search-type");
      if (this.isTypeaheadSearch(searchType)) {
        this.setTypeaheadOpen(selectElement, false);
      } else {
        selectElement
          .querySelector(domUtils.getSelectTriggerSelector(selectElement))
          ?.setAttribute("aria-expanded", "false");
        if (!domUtils.isCustomSelect(selectElement)) {
          const searchInput = selectElement.querySelector(
            constants.SELECTORS.searchInput
          );
          if (searchInput) searchInput.value = "";
        }
      }
    },

    closeAllSelects: function () {
      const constants = window.BoaBranchPicker.constants;
      const domUtils = window.BoaBranchPicker.domUtils;

      const modal = domUtils.getModal();
      if (!modal) return;
      modal
        .querySelectorAll(
          `${constants.SELECTORS.customSelect}[data-open], ${constants.SELECTORS.searchableSelect}[data-open]`
        )
        .forEach((openSelect) => this.closeSelect(openSelect));
      modal
        .querySelectorAll("[data-address-dropdown][data-open]")
        .forEach((dropdown) => dropdown.removeAttribute("data-open"));
    },

    selectOption: function (selectElement, optionElement, isCustom) {
      const constants = window.BoaBranchPicker.constants;

      const optionValue = optionElement?.getAttribute("data-value");
      const optionText = optionElement?.textContent.trim();
      if (!selectElement || !optionElement || !optionValue) return;

      const searchType = selectElement.getAttribute("data-search-type");
      const valueDisplay = selectElement.querySelector(
        isCustom
          ? constants.SELECTORS.customValue
          : constants.SELECTORS.searchableValue
      );
      const optionSelector = isCustom
        ? constants.SELECTORS.customOption
        : constants.SELECTORS.searchableOption;

      if (this.isTypeaheadSearch(searchType)) {
        const hiddenInput = selectElement.querySelector("input[type='hidden']");
        if (hiddenInput instanceof HTMLInputElement) {
          hiddenInput.value = optionValue;
          hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
        const searchInput = selectElement.querySelector(
          constants.SELECTORS.searchInput
        );
        if (searchInput instanceof HTMLInputElement) {
          searchInput.value = optionValue;
        }
        selectElement.closest(".input")?.classList.add("is-filled");
        if (searchType === "city") {
          window.BoaBranchPicker.addressUtils?.handleCitySelected(optionValue);
        }
      } else {
        const hiddenInput = selectElement.querySelector(
          isCustom
            ? constants.SELECTORS.customHiddenInput
            : constants.SELECTORS.searchableHiddenInput
        );
        if (hiddenInput) {
          hiddenInput.value = optionValue;
          hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
          if (hiddenInput.id === "boa-pickup-region") {
            const addressUtils = window.BoaBranchPicker.addressUtils;
            if (addressUtils?.updateMetaobjectId) {
              addressUtils.updateMetaobjectId(
                constants.IDS.regionMetaobjectId,
                optionElement.getAttribute("data-region-metaobject-id")
              );
            }
          } else if (hiddenInput.id === "boa-pickup-branch") {
            const addressUtils = window.BoaBranchPicker.addressUtils;
            if (addressUtils?.updateMetaobjectId) {
              addressUtils.updateMetaobjectId(
                constants.IDS.branchMetaobjectId,
                optionElement.getAttribute("data-branch-metaobject-id")
              );
            }
          }
        }
        if (valueDisplay) valueDisplay.textContent = optionText;
        selectElement.closest(".input")?.classList.add("is-filled");
      }

      selectElement
        .querySelectorAll(optionSelector)
        .forEach((option) => option.setAttribute("aria-selected", "false"));
      optionElement.setAttribute("aria-selected", "true");
      this.closeSelect(selectElement);
      const formUtils = window.BoaBranchPicker.formUtils;
      if (formUtils && formUtils.updateSubmitButtonState) {
        formUtils.updateSubmitButtonState();
      }
      return { value: optionValue, select: selectElement };
    },

    handleHospitalSelection: function (optionElement) {
      const constants = window.BoaBranchPicker.constants;

      const template = optionElement?.querySelector("template");
      const departmentCustomSelect = document.getElementById(
        constants.IDS.departmentSelect
      );
      const departmentInput = departmentCustomSelect?.querySelector(
        constants.SELECTORS.customHiddenInput
      );
      const dropdown = departmentCustomSelect?.querySelector(
        constants.SELECTORS.customDropdown
      );
      if (
        !template ||
        !departmentCustomSelect ||
        !(departmentInput instanceof HTMLInputElement) ||
        !dropdown
      )
        return;

      departmentInput.value = "";
      this.setFieldState(departmentInput, departmentCustomSelect, true);
      dropdown.innerHTML = "";
      dropdown.appendChild(template.content.cloneNode(true));
    },

    getSearchData: function (searchType) {
      if (searchType === "hospital") {
        const constants = window.BoaBranchPicker.constants;
        const domUtils = window.BoaBranchPicker.domUtils;

        const modal = domUtils.getModal();
        const dateStep = modal?.querySelector(constants.DATE_STEP);
        if (!dateStep) return [];
        const hospitalOptions = dateStep.querySelectorAll(
          '[data-search-type="hospital"] [data-search-results] .boa-searchable-select__option'
        );
        return Array.from(hospitalOptions).map((option) => ({
          value: option.getAttribute("data-value"),
          label: option.textContent.trim(),
          departments: option.getAttribute("data-departments"),
        }));
      }
      return [];
    },

    filterItems: function (items, query) {
      if (!query) return items;
      const lowerQuery = query.toLowerCase();
      return items.filter((searchItem) =>
        searchItem.label.toLowerCase().includes(lowerQuery)
      );
    },

    clearSearchResults: function (container) {
      if (!container) return;
      const loadingIndicator = container.querySelector(
        "[data-loading-indicator]"
      );
      const options = container.querySelectorAll(
        ".boa-searchable-select__option"
      );
      options.forEach((opt) => opt.remove());
      if (loadingIndicator && !container.contains(loadingIndicator)) {
        container.appendChild(loadingIndicator);
      }
    },

    buildHighlightedLabel: function (label, matchedSubstrings) {
      if (!matchedSubstrings?.length || !Array.isArray(matchedSubstrings)) {
        return document.createTextNode(label);
      }

      const labelLength = label.length;
      const sortedMatches = [...matchedSubstrings]
        .map((substringMatch) => {
          const clampedOffset = Math.max(
            0,
            Math.min(substringMatch.offset, labelLength)
          );
          const maxLength = labelLength - clampedOffset;
          const clampedLength = Math.max(
            0,
            Math.min(substringMatch.length, maxLength)
          );
          return { offset: clampedOffset, length: clampedLength };
        })
        .filter((substringMatch) => substringMatch.length > 0)
        .sort(
          (firstMatch, secondMatch) => firstMatch.offset - secondMatch.offset
        );

      if (sortedMatches.length === 0) {
        return document.createTextNode(label);
      }

      const fragment = document.createDocumentFragment();
      let lastIndex = 0;

      for (const substringMatch of sortedMatches) {
        if (substringMatch.offset > lastIndex) {
          fragment.appendChild(
            document.createTextNode(
              label.substring(lastIndex, substringMatch.offset)
            )
          );
        }

        const highlightMark = document.createElement("mark");
        highlightMark.appendChild(
          document.createTextNode(
            label.substring(
              substringMatch.offset,
              substringMatch.offset + substringMatch.length
            )
          )
        );
        fragment.appendChild(highlightMark);

        lastIndex = substringMatch.offset + substringMatch.length;
      }

      if (lastIndex < label.length) {
        fragment.appendChild(
          document.createTextNode(label.substring(lastIndex))
        );
      }

      return fragment;
    },

    renderSearchResults: function (resultsContainer, items) {
      if (!resultsContainer) return;
      this.clearSearchResults(resultsContainer);
      // aria-activedescendant can only point at an id, so every option needs one.
      const listboxId = resultsContainer.closest("[role='listbox']")?.id;
      items.forEach((searchItem, index) => {
        const optionButton = document.createElement("button");
        optionButton.type = "button";
        optionButton.className = "boa-searchable-select__option";
        optionButton.setAttribute("role", "option");
        if (listboxId) {
          optionButton.id = `${listboxId}-option-${index}`;
        }
        optionButton.setAttribute(
          "data-value",
          searchItem.value || searchItem.id
        );
        if (searchItem.departments)
          optionButton.setAttribute("data-departments", searchItem.departments);

        optionButton.appendChild(
          this.buildHighlightedLabel(
            searchItem.label,
            searchItem.matchedSubstrings
          )
        );
        resultsContainer.appendChild(optionButton);
      });
    },

    loadSearchResults: async function (searchableSelect, query, searchType) {
      const constants = window.BoaBranchPicker.constants;

      let resultsContainer;
      if (this.isTypeaheadSearch(searchType)) {
        resultsContainer = this.getTypeaheadDropdown(
          searchableSelect
        )?.querySelector(constants.SELECTORS.searchResults);
      } else {
        resultsContainer = searchableSelect.querySelector(
          constants.SELECTORS.searchResults
        );
      }
      if (!resultsContainer) {
        return;
      }

      if (searchType === "hospital") {
        const originalOptions = resultsContainer.querySelectorAll(
          constants.SELECTORS.searchableOption
        );
        const lowerQuery = query.toLowerCase();

        originalOptions.forEach((hospitalOption) => {
          const optionText = hospitalOption.textContent.trim().toLowerCase();
          const matches = !query || optionText.includes(lowerQuery);
          hospitalOption.style.display = matches ? "" : "none";
        });
        return;
      }

      if (this.isTypeaheadSearch(searchType)) {
        const addressData = window.BoaBranchPicker.addressData;
        const dropdown = this.getTypeaheadDropdown(searchableSelect);
        const loadingIndicator = resultsContainer.querySelector(
          "[data-loading-indicator]"
        );
        const searchInput = searchableSelect.querySelector(
          constants.SELECTORS.searchInput
        );

        // Streets are already scoped to one city, so an empty query lists them. Cities are
        // not scoped by anything, so listing 1,400 of them on focus is just noise.
        if (!query && searchType === "city") {
          this.clearSearchResults(resultsContainer);
          this.setTypeaheadOpen(searchableSelect, false);
          return;
        }

        let isStale = false;
        this.setTypeaheadOpen(searchableSelect, true);
        resultsContainer.classList.add("is-loading");
        this.clearSearchResults(resultsContainer);
        if (loadingIndicator) loadingIndicator.removeAttribute("hidden");

        try {
          const selectedCity = this.getSelectedCity();
          const values =
            searchType === "city"
              ? await addressData.getCities(addressData.detectLang(query))
              : selectedCity
                ? await addressData.getStreets(selectedCity)
                : [];

          // The list arrives asynchronously on first use. Drop it if the query moved on, or
          // if the city did — clearing the street blanks the query, so it alone can match.
          // A stale call must also leave the loading UI to the newer one still in flight.
          isStale =
            (searchInput && searchInput.value.trim() !== query) ||
            (searchType === "street" &&
              this.getSelectedCity() !== selectedCity);
          if (isStale) {
            return;
          }

          const matches = addressData.filterMatches(values, query);
          this.renderSearchResults(resultsContainer, matches);
          if (!matches.length) {
            this.setTypeaheadOpen(searchableSelect, false);
          }
        } catch (error) {
          this.clearSearchResults(resultsContainer);
          console.error("Error loading address options:", error);
        } finally {
          if (!isStale) {
            resultsContainer.classList.remove("is-loading");
            if (loadingIndicator) loadingIndicator.setAttribute("hidden", "");
          }
        }
        return;
      }

      const searchData = this.getSearchData(searchType);
      if (!searchData.length) {
        this.clearSearchResults(resultsContainer);
        return;
      }
      this.renderSearchResults(
        resultsContainer,
        this.filterItems(searchData, query)
      );
    },
  };

  // ============================================================================
  // Address Utils
  // ============================================================================
  window.BoaBranchPicker.addressUtils = {
    getStreetSelect: function () {
      const constants = window.BoaBranchPicker.constants;
      const domUtils = window.BoaBranchPicker.domUtils;
      const dateStep = domUtils
        .getModal()
        ?.querySelector(constants.DATE_STEP);
      return dateStep?.querySelector('[data-search-type="street"]') || null;
    },

    clearStreet: function () {
      const constants = window.BoaBranchPicker.constants;
      const domUtils = window.BoaBranchPicker.domUtils;
      const selectUtils = window.BoaBranchPicker.selectUtils;

      const streetInput = domUtils.getInputElement(constants.IDS.streetInput);
      const streetSelect = this.getStreetSelect();
      if (streetInput) {
        streetInput.value = "";
      }
      if (streetSelect) {
        selectUtils.resetSearchableSelectValue(streetSelect, streetInput);
        // Closes the panel too: a street list still loading has no newer call to hand its
        // spinner to, so it would sit open over the disabled field.
        selectUtils.closeSelect(streetSelect);
        selectUtils.setFieldState(streetInput, streetSelect, false);
      }
    },

    handleCitySelected: async function (city) {
      const addressData = window.BoaBranchPicker.addressData;
      const constants = window.BoaBranchPicker.constants;
      const domUtils = window.BoaBranchPicker.domUtils;
      const formUtils = window.BoaBranchPicker.formUtils;
      const selectUtils = window.BoaBranchPicker.selectUtils;

      this.clearStreet();

      const streetInput = domUtils.getInputElement(constants.IDS.streetInput);
      const streetSelect = this.getStreetSelect();
      if (streetSelect) {
        selectUtils.setFieldState(streetInput, streetSelect, true);
      }

      let streets = [];
      try {
        streets = await addressData.getStreets(city);
      } catch (error) {
        console.error("Error loading streets:", error);
        return;
      }

      // A second city picked while this list was loading owns the field now.
      if (selectUtils.getSelectedCity() !== city) {
        return;
      }

      // Verifone gives many settlements a single "coordinate on arrival" street; picking it
      // for the customer saves a pointless step, and the same holds for any one-street city.
      if (streets.length === 1 && streetSelect) {
        const searchInput = streetSelect.querySelector(
          constants.SELECTORS.searchInput
        );
        if (streetInput) {
          streetInput.value = streets[0];
        }
        if (searchInput instanceof HTMLInputElement) {
          searchInput.value = streets[0];
        }
        streetSelect.closest(".input")?.classList.add("is-filled");
        formUtils?.updateSubmitButtonState();
      }
    },

    updateMetaobjectId: function (inputId, metaobjectId) {
      const domUtils = window.BoaBranchPicker.domUtils;

      const input = domUtils.getInputElement(inputId);
      if (input && metaobjectId) {
        input.value = metaobjectId;
      }
    },

    enableRecipientFields: function (recipientType, dateStep) {
      const constants = window.BoaBranchPicker.constants;
      const domUtils = window.BoaBranchPicker.domUtils;
      const selectUtils = window.BoaBranchPicker.selectUtils;

      if (recipientType === "private") {
        // Street stays disabled until a city is chosen — it is scoped to that city.
        const cityInput = domUtils.getInputElement(constants.IDS.cityInput);
        const citySelect = dateStep?.querySelector('[data-search-type="city"]');
        if (citySelect?.hasAttribute("data-disabled")) {
          if (selectUtils?.setFieldState) {
            selectUtils.setFieldState(cityInput, citySelect, true);
          }
        }
      } else if (recipientType === "hospital") {
        const hospitalInput = domUtils.getInputElement(
          constants.IDS.hospitalInput
        );
        const hospitalSelect = hospitalInput?.closest(
          constants.SELECTORS.searchableSelect
        );
        if (hospitalInput && hospitalSelect?.hasAttribute("data-disabled")) {
          if (selectUtils?.setFieldState) {
            selectUtils.setFieldState(hospitalInput, hospitalSelect, true);
          }
        }
        const departmentCustomSelect = document.getElementById(
          constants.IDS.departmentSelect
        );
        const departmentInput = departmentCustomSelect?.querySelector(
          constants.SELECTORS.customHiddenInput
        );
        if (
          departmentInput instanceof HTMLInputElement &&
          hospitalInput &&
          hospitalInput.value &&
          departmentCustomSelect?.hasAttribute("data-disabled")
        ) {
          if (selectUtils?.setFieldState) {
            selectUtils.setFieldState(
              departmentInput,
              departmentCustomSelect,
              true
            );
          }
          const hospitalOptions = dateStep.querySelectorAll(
            '[data-search-type="hospital"] .boa-searchable-select__option'
          );
          const hospitalOption = Array.from(hospitalOptions).find(
            (option) =>
              option.getAttribute("data-value") === hospitalInput.value
          );
          const template = hospitalOption?.querySelector("template");
          const dropdown = departmentCustomSelect.querySelector(
            constants.SELECTORS.customDropdown
          );
          if (template && dropdown) {
            dropdown.innerHTML = "";
            dropdown.appendChild(template.content.cloneNode(true));
          }
        }
      }
    },

    resetAddressFields: function (modal, type) {
      const constants = window.BoaBranchPicker.constants;
      const domUtils = window.BoaBranchPicker.domUtils;
      const selectUtils = window.BoaBranchPicker.selectUtils;

      const dateStep = modal?.querySelector(constants.DATE_STEP);
      if (!dateStep) return;

      if (type === "private") {
        const houseNumberInput = domUtils.getInputElement(
          constants.IDS.houseNumberInput
        );

        [
          { id: constants.IDS.cityInput, searchType: "city" },
          { id: constants.IDS.streetInput, searchType: "street" },
        ].forEach((field) => {
          const hiddenInput = domUtils.getInputElement(field.id);
          const select = dateStep.querySelector(
            `[data-search-type="${field.searchType}"]`
          );
          const searchInput = select?.querySelector(
            constants.SELECTORS.searchInput
          );

          if (hiddenInput) hiddenInput.value = "";
          if (searchInput instanceof HTMLInputElement) {
            searchInput.value = "";
          }
          if (select) {
            select.closest(".input")?.classList.remove("is-filled");
            // The dropdown is a sibling of the collapsed fields, so it stays visible and
            // clickable unless closed here.
            selectUtils?.closeSelect(select);
            if (selectUtils?.setFieldState) {
              selectUtils.setFieldState(hiddenInput, select, false);
            }
          }
        });

        if (houseNumberInput) {
          houseNumberInput.value = "";
          houseNumberInput.closest(".input")?.classList.remove("is-filled");
        }
      } else if (type === "hospital") {
        const hospitalInput = domUtils.getInputElement(
          constants.IDS.hospitalInput
        );
        const hospitalSelect = hospitalInput?.closest(
          constants.SELECTORS.searchableSelect
        );
        const departmentCustomSelect = document.getElementById(
          constants.IDS.departmentSelect
        );
        const departmentInput = departmentCustomSelect?.querySelector(
          constants.SELECTORS.customHiddenInput
        );
        const buildingInput = dateStep.querySelector("#boa-delivery-building");
        const floorInput = dateStep.querySelector("#boa-delivery-floor");

        if (hospitalInput && hospitalSelect) {
          hospitalInput.value = "";
          if (selectUtils?.resetSearchableSelectValue) {
            selectUtils.resetSearchableSelectValue(
              hospitalSelect,
              hospitalInput
            );
          }
          hospitalSelect.closest(".input")?.classList.remove("is-filled");
        }
        if (
          departmentInput instanceof HTMLInputElement &&
          departmentCustomSelect
        ) {
          if (selectUtils?.resetCustomSelectValue) {
            selectUtils.resetCustomSelectValue(
              departmentCustomSelect,
              departmentInput
            );
          }
          if (selectUtils?.setFieldState) {
            selectUtils.setFieldState(
              departmentInput,
              departmentCustomSelect,
              false
            );
          }
          departmentCustomSelect
            .closest(".input")
            ?.classList.remove("is-filled");
          const dropdown = departmentCustomSelect.querySelector(
            constants.SELECTORS.customDropdown
          );
          if (dropdown) dropdown.innerHTML = "";
        }
        if (buildingInput instanceof HTMLInputElement) buildingInput.value = "";
        if (floorInput instanceof HTMLInputElement) floorInput.value = "";
      }
    },
  };

  // ============================================================================
  // Cart Utils
  // ============================================================================
  // Maps cart-level branch/delivery attributes to hidden (_-prefixed) line-item
  // property names. Shared by the cart stamping below and the add-to-cart
  // interceptor so both produce an identical property set.
  const BRANCH_LINE_ITEM_PROPERTY_MAPPING = [
    { cart: "delivery_date", item: "_delivery_date" },
    { cart: "address_type", item: "_address_type" },
    { cart: "branch_verifone_id", item: "_branch_verifone_id" },
    { cart: "branch_metaobject_id", item: "_branch_metaobject_id" },
    { cart: "region_metaobject_id", item: "_region_metaobject_id" },
    { cart: "private_address_city", item: "_private_address_city" },
    { cart: "private_address_address1", item: "_private_address_address1" },
    { cart: "hospital_name", item: "_hospital_name" },
    { cart: "hospital_department", item: "_hospital_department" },
    { cart: "hospital_building", item: "_hospital_building" },
    { cart: "hospital_floor", item: "_hospital_floor" },
    { cart: "delivery_price", item: "_delivery_price" },
    { cart: "zip_code", item: "_zip_code" },
    { cart: "is_special_date", item: "_is_special_date" },
  ];

  window.BoaBranchPicker.cartUtils = {
    // Empty values are skipped: cart/add.js drops blank-valued properties, so to
    // let repeat adds merge, add.js and change.js must yield the same set.
    buildLineItemProperties: function (attributes) {
      const properties = {};
      for (const { cart, item } of BRANCH_LINE_ITEM_PROPERTY_MAPPING) {
        const value = attributes?.[cart];
        if (value) {
          properties[item] = value;
        }
      }
      // B2B: charge the negotiated company price, locked against the live Verifone price.
      // Server-rendered signal (js-variables.liquid) - the branch Verifone price never applies.
      const b2b = window.themeVariables?.b2b;
      if (b2b?.deliveryPriceLocked && b2b?.deliveryPrice) {
        properties["_delivery_price"] = String(b2b.deliveryPrice);
        properties["_delivery_price_locked"] = "true";
      }
      return properties;
    },
    branchPropertiesNeedUpdate: function (itemProperties, branchPickerProperties) {
      for (const [key, value] of Object.entries(branchPickerProperties)) {
        if (String(itemProperties?.[key] ?? "") !== String(value)) {
          return true;
        }
      }
      return false;
    },
    updateCartItemProperties: async function (cartContent) {
      if (!cartContent) {
        cartContent = await (
          await fetch(`${window["themeVariables"].routes.cartUrl}.js`, {
            cache: "reload",
          })
        ).json();
      }
      const attributes = cartContent.attributes;
      if (!attributes?.address_type) {
        return;
      }

      const branchPickerProperties = this.buildLineItemProperties(attributes);

      for (const cartItem of cartContent?.items) {
        // Nested vase children must not be changed here — change.js with partial
        // properties breaks parent/child links and can remove bundled vases.
        if (cartItem.parent_relationship) {
          continue;
        }

        const mergedProperties = {
          ...(cartItem.properties || {}),
          ...branchPickerProperties,
        };

        if (!this.branchPropertiesNeedUpdate(cartItem.properties, branchPickerProperties)) {
          continue;
        }

        try {
          const response = await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              id: cartItem.key,
              // quantity is required: change.js resets the line to 1 if omitted
              quantity: cartItem.quantity,
              properties: mergedProperties,
            }),
          });
          if (!response.ok) {
            console.error(
              `Failed to update cart item properties for item ${cartItem.key}: ${response.status}`
            );
          }
        } catch (error) {
          console.error(
            `Error updating cart item properties for item ${cartItem.key}:`,
            error
          );
        }
      }
    },
  };

  // Dispatch ready event
  document.dispatchEvent(new CustomEvent("boa-branch-picker:utils-ready"));
})();
